import axios from 'axios';

const DEFAULT_FILE_API_BASE = 'http://127.0.0.1:3021';

declare global {
  interface Window {
    /** iframe·임베드: 번들보다 먼저 실행되는 인라인 스크립트에서 설정 */
    __SLOT2_API_BASE__?: string;
    /** 레거시 번들 호환 */
    SLOT2_API_BASE?: string;
  }
}

/**
 * http(s) 로 열었는데 호스트에 `/api` 프록시가 없으면 404 → `VITE_SLOT2_API_BASE` 또는 `__SLOT2_API_BASE__`.
 * file:// 은 절대 URL 폴백.
 */
function resolveApiBaseURL(): string {
  if (typeof window !== 'undefined') {
    const injected = String(window.__SLOT2_API_BASE__ ?? window.SLOT2_API_BASE ?? '').trim();
    if (injected) return injected.replace(/\/$/, '');
  }
  const u = import.meta.env.VITE_SLOT2_API_BASE?.trim();
  if (u) return u.replace(/\/$/, '');
  if (typeof globalThis !== 'undefined' && globalThis.location?.protocol === 'file:') {
    return DEFAULT_FILE_API_BASE;
  }
  return '/api';
}

const client = axios.create({
  baseURL: resolveApiBaseURL(),
  timeout: 20000,
  headers: { Accept: 'application/json' },
  withCredentials: import.meta.env.VITE_SLOT2_WITH_CREDENTIALS === '1',
});

export interface LatestEthPayload {
  blockNumberDec: number;
  blockNumberHex?: string;
  prevEvenBlock?: number;
  latestEvenBlock?: number;
  error?: string;
}

export interface BlockEthPayload {
  block: number;
  blockNumberDec?: number;
  randomnessHex?: string;
  isOdd?: boolean;
  parityValue?: number;
  error?: string;
}

export function getEthBasePath(): string {
  const p = import.meta.env.VITE_SLOT2_ETH_PATH?.trim();
  return p || '/slot2/eth';
}

/** 최신 블록 번호(JSON) — PHP eth_candle_result.php 또는 slot2API /slot2/eth */
export async function fetchEthLatest(): Promise<LatestEthPayload | null> {
  const path = getEthBasePath();
  const sep = path.includes('?') ? '&' : '?';
  const { data, status } = await client.get<LatestEthPayload>(`${path}${sep}_t=${Date.now()}`, {
    validateStatus: () => true,
    headers: { 'Cache-Control': 'no-store' },
  });
  if (status !== 200 || !data || (data as { error?: string }).error) return null;
  if (typeof data.blockNumberDec !== 'number') return null;
  return data;
}

export async function fetchEthBlock(blockNum: number): Promise<BlockEthPayload | null> {
  const path = getEthBasePath();
  const sep = path.includes('?') ? '&' : '?';
  const { data, status } = await client.get<BlockEthPayload>(
    `${path}${sep}block=${encodeURIComponent(String(blockNum))}`,
    { validateStatus: () => true }
  );
  if (status !== 200 || !data || data.error) return null;
  return data;
}

function historyPath(): string {
  return import.meta.env.VITE_SLOT2_HISTORY_PATH?.trim() || '/slot2/history';
}

export async function fetchSlot2HistoryRecent(limit: number): Promise<{
  success?: boolean | number | string;
  items?: Array<{ round: number; result: string; randomnessHex?: string; randomness_hex?: string }>;
} | null> {
  try {
    const { data, status } = await client.get(historyPath(), {
      params: { action: 'recent', limit },
      validateStatus: () => true,
    });
    if (status !== 200 || !data || typeof data !== 'object') return null;
    return data as {
      success?: boolean;
      items?: Array<{ round: number; result: string; randomnessHex?: string; randomness_hex?: string }>;
    };
  } catch {
    return null;
  }
}
